import { Controller } from "@hotwired/stimulus"
import { claim, release } from "../lib/audio_preview_bus"

// Playing a band's thirty seconds without opening its profile - the "Listen"/"Posłuchaj"
// CTA in the profile header today and, later, the player on a slot application. The transport,
// progress bar and counter are the same everywhere; only the surrounding markup differs.
//
// Enhancement only. The server renders a plain <audio controls> with the clip, which works
// on its own; this hides those controls and reveals our own panel once it is up.
//
// A clip starts on a press and on nothing else. The opt-in hover-to-play went with the call
// #124 reversed, rather than staying here as an option no caller can take.
export default class extends Controller {
  static targets = [
    "audio", "panel", "toggle", "playIcon", "pauseIcon", "loading", "label", "progress", "track", "fill", "time"
  ]
  static values = {
    clipSeconds: { type: Number, default: 30 },
    // A CTA is a button until it is pressed: the bar and the counter appear with the first note
    // and fold away again once the clip has run out.
    revealOnPlay: Boolean,
    seekable: Boolean,
    playLabel: String,
    pauseLabel: String
  }

  connect() {
    // Our transport drives the element from here on; the native controls would be a second set
    // of buttons saying something different.
    this.audioTarget.controls = false
    this.panelTarget.classList.remove("hidden")
    this.#render()
  }

  disconnect() {
    // A Turbo navigation takes the markup away, and an element detached from the document goes
    // on playing perfectly happily if nobody stops it.
    this.audioTarget.pause()
    this.#finishLoading()
    release(this.audioTarget)
  }

  // Playback events ------------------------------------------------------------------------

  started() {
    this.#finishLoading()
    claim(this.audioTarget)
    this.#render()
  }

  changed() {
    this.#render()
  }

  failed() {
    this.#finishLoading()
    this.#render()
  }

  // Interaction ------------------------------------------------------------------------------

  toggle() {
    if (this.loading) {
      this.audioTarget.pause()
      return this.#finishLoading()
    }
    if (!this.audioTarget.paused) return this.audioTarget.pause()

    this.#play()
  }

  seek(event) {
    const track = this.trackTarget.getBoundingClientRect()
    if (!track.width) return

    this.#moveTo(((event.clientX - track.left) / track.width) * this.#length())
  }

  // The seekable bar is a slider, so it answers the keys a slider answers. The transport
  // button is a real <button> and needs nothing from us - Enter and Space already work.
  key(event) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault()
      return this.toggle()
    }

    const moves = {
      ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5,
      Home: -this.#length(), End: this.#length()
    }
    if (!(event.key in moves)) return

    event.preventDefault()
    this.#moveTo(this.#position() + moves[event.key])
  }

  // Playback -------------------------------------------------------------------------------

  #play() {
    const audio = this.audioTarget

    // Nothing is fetched until there is a reason to - the grid loads a page full of these.
    audio.preload = "auto"
    if (this.#position() >= this.#length()) audio.currentTime = 0
    this.loading = true
    this.element.setAttribute("aria-busy", "true")
    this.loadingTarget.classList.remove("hidden")
    this.playIconTarget.classList.add("hidden")

    // A rejected play() is an uncaught error rather than a quiet no - and the keyboard can reach
    // this transport too, on a page the browser may not consider gestured yet. The button stays
    // where it is; pressing it is the gesture.
    audio.play().catch((error) => {
      console.warn("[audio-preview] the browser would not start this clip", error)
      this.#finishLoading()
      this.#render()
    })
  }

  #moveTo(seconds) {
    const audio = this.audioTarget
    const at = Math.min(Math.max(seconds, 0), this.#length())

    // A clip nobody has played yet has no timeline to move along: with `preload="none"` not a
    // byte has been fetched, and a currentTime set in that state is dropped on the floor. Ask for
    // the file and place the playhead once it knows the song.
    if (audio.readyState === HTMLMediaElement.HAVE_NOTHING) {
      audio.preload = "auto"
      audio.addEventListener("loadedmetadata", () => { audio.currentTime = at }, { once: true })
      audio.load()
      return
    }

    audio.currentTime = at
  }

  #finishLoading() {
    this.loading = false
    this.element.removeAttribute("aria-busy")
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
  }

  #render() {
    const audio = this.audioTarget
    const length = this.#length()
    const at = this.#position()

    // Back to the start once it is over, rather than a full bar reading 0:30 / 0:30 that the next
    // press would have to rewind anyway. The `!paused` half is for a clip longer than the window
    // we promise (a file replaced under us): it would run past 0:30, so the window is what ends it.
    if (audio.ended || (!audio.paused && at >= length)) {
      audio.pause()
      audio.currentTime = 0
      return
    }

    const playing = !audio.paused
    const readout = `${clock(at)} / ${clock(length)}`

    this.fillTarget.style.width = `${length ? (at / length) * 100 : 0}%`
    this.timeTarget.textContent = readout
    this.playIconTarget.classList.toggle("hidden", playing || this.loading)
    this.pauseIconTarget.classList.toggle("hidden", !playing)
    this.toggleTarget.setAttribute("aria-label", playing ? this.pauseLabelValue : this.playLabelValue)
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = playing ? this.pauseLabelValue : this.playLabelValue
    }

    // Anything to report is playing or paused part-way in; a clip sitting at 0:00 has not been
    // touched, and the CTA goes back to being a button.
    if (this.revealOnPlayValue) {
      this.progressTarget.classList.toggle("hidden", !playing && at === 0)
    }

    this.trackTarget.setAttribute("aria-valuemax", Math.round(length))
    this.trackTarget.setAttribute("aria-valuenow", Math.round(at))
    this.trackTarget.setAttribute("aria-valuetext", readout)
  }

  // A song shorter than the clip is all clip; before the metadata arrives, what we promised.
  #length() {
    const duration = this.audioTarget.duration

    return Number.isFinite(duration) && duration > 0
      ? Math.min(duration, this.clipSecondsValue)
      : this.clipSecondsValue
  }

  #position() {
    return Math.min(this.audioTarget.currentTime, this.#length())
  }
}

function clock(seconds) {
  if (!Number.isFinite(seconds)) return "0:00"

  const total = Math.max(0, Math.floor(seconds))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}
